import {
  ServiceProviderUser,
  CreateServiceProviderUserRequest,
  UpdateServiceProviderUserRequest,
  ServiceProviderUserFilters,
  PaginatedServiceProviderUsers,
  UserRole,
  InvitationStatus,
} from '../types';
import { HttpClient, ApiResponse } from './httpClient';
import { env } from '@/config/env';

/**
 * Service class for managing service provider users
 */
class ServiceProviderUserService {
  /**
   * Wrapper for HttpClient.fetchWithAuth that prefixes the User Service base URL.
   * Returns ApiResponse<T> directly from the HTTP client without additional type assertions.
   * @param endpoint - API endpoint path (will be prefixed with userServiceBaseUrl)
   * @param options - Fetch request options
   * @returns Promise resolving to ApiResponse<T> containing the response data
   */
  private async fetchWithAuth<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    return HttpClient.fetchWithAuth<T>(`${env.userServiceBaseUrl}${endpoint}`, options);
  }

  /**
   * Normalize array of IDs to lowercase strings for API
   */
  private normalizeIds(ids: string[]): string[] {
    return ids.map(id => String(id).toLowerCase());
  }

  /**
   * Normalize a date-of-birth value to a full ISO string, or null when absent.
   * Appends T12:00:00.000Z to date-only strings to avoid timezone drift.
   */
  private normalizeDateOfBirth(dateOfBirth: string | undefined | null): string | null {
    if (!dateOfBirth) return null;
    return dateOfBirth.includes('T') ? dateOfBirth : `${dateOfBirth}T12:00:00.000Z`;
  }

  /**
   * Fetch External users only (Service Provider users), paginated on the server.
   * page is 0-based (MUI DataGrid convention); the server expects 1-based pageNumber.
   */
  async fetchUsers(
    filters: ServiceProviderUserFilters = {},
    page: number = 0,
    pageSize: number = 100
  ): Promise<PaginatedServiceProviderUsers> {
    const paged = await HttpClient.fetchUserPage(
      this.fetchWithAuth.bind(this),
      'External',
      page,
      pageSize,
      {
        search: filters.search,
        isActive: filters.isActive,
        roles: filters.roles,
        serviceProviderIds: filters.serviceProviderIds,
        statuses: filters.statuses,
      }
    );
    // The list endpoint returns a bare PaginatedResult on 200, but fetchWithAuth
    // yields { success: true } (no items) for a 204/non-JSON body — guard so
    // .map/.forEach can't throw. Parity with serviceProviderService.
    const items = Array.isArray(paged?.items) ? paged.items : [];

    const mappedUsers: ServiceProviderUser[] = items.map((apiUser) =>
      this.mapApiUserToServiceProviderUser(apiUser));

    // collectedGuids only reflect the current page — the location scope layer
    // resolves any GUID names it doesn't already have in the map lazily.
    const allStateGuids = new Set<string>();
    const allDistrictGuids = new Set<string>();
    const allDispatchAreaGuids = new Set<string>();

    items.forEach((user: any) => {
      if (Array.isArray(user.authorizedStates)) {
        user.authorizedStates.forEach((guid: string) => {
          if (guid) allStateGuids.add(guid);
        });
      }
      if (Array.isArray(user.authorizedDistricts)) {
        user.authorizedDistricts.forEach((guid: string) => {
          if (guid) allDistrictGuids.add(guid);
        });
      }
      if (Array.isArray(user.authorizedDispatchAreas)) {
        user.authorizedDispatchAreas.forEach((guid: string) => {
          if (guid) allDispatchAreaGuids.add(guid);
        });
      }
    });

    return {
      users: mappedUsers,
      total: paged.totalCount,
      page: paged.pageNumber - 1,
      pageSize: paged.pageSize,
      totalPages: paged.totalPages,
      collectedGuids: {
        states: Array.from(allStateGuids),
        districts: Array.from(allDistrictGuids),
        dispatchAreas: Array.from(allDispatchAreaGuids)
      }
    };
  }


  /**
   * Create a new service provider user
   */
  async createUser(userData: CreateServiceProviderUserRequest): Promise<ServiceProviderUser> {
    // 🚀 SERVICE: Creating user with data: userData
    // 🚀 SERVICE: JSON payload: JSON.stringify(userData, null, 2)
    
    // Compute name from firstName and lastName if provided
    const computedName = userData.firstName && userData.lastName 
      ? `${userData.firstName.trim()} ${userData.lastName.trim()}`.trim()
      : userData.name || '';

    const formattedDateOfBirth = this.normalizeDateOfBirth(userData.dateOfBirth);
    
    // Build payload to match the API format - use camelCase as expected
    const apiPayload = {
      firstName: userData.firstName,
      lastName: userData.lastName,
      name: computedName,
      email: userData.email,
      mobileNumber: userData.mobileNumber || "5551110000",
      userType: userData.userType || "External",
      userRoles: userData.userRoles || ["Driver"], // Array of role names matching UserRole enum
      dateOfBirth: formattedDateOfBirth,
      hireDate: userData.hireDate || new Date().toISOString().split('T')[0] + "T00:00:00Z",
      // Only include authorization arrays if they have values
      ...(userData.serviceProviderId && { serviceProviderId: userData.serviceProviderId }),
      ...(userData.authorizedStates && userData.authorizedStates.length > 0 && { 
        authorizedStates: this.normalizeIds(userData.authorizedStates)
      }),
      ...(userData.authorizedDistricts && userData.authorizedDistricts.length > 0 && { 
        authorizedDistricts: this.normalizeIds(userData.authorizedDistricts)
      }),
      ...(userData.authorizedDispatchAreas && userData.authorizedDispatchAreas.length > 0 && { 
        authorizedDispatchAreas: this.normalizeIds(userData.authorizedDispatchAreas)
      })
    };



    try {
      const response = await this.fetchWithAuth<ServiceProviderUser>(
        '/Users',
        {
          method: 'POST',
          body: JSON.stringify(apiPayload),
        }
      );



      // Support both response formats: { data: user } or direct user object
      if (!response) {
        throw new Error('API response is empty');
      }

      // If response has a data property, use it; otherwise use response directly
      return response.data || (response as unknown as ServiceProviderUser);
    } catch (error: any) {
      // Handle email conflict errors from API
      if (error?.status === 409 || 
          error?.message?.toLowerCase().includes('email') && error?.message?.toLowerCase().includes('exist') ||
          error?.message?.toLowerCase().includes('duplicate') ||
          error?.message?.toLowerCase().includes('already')) {
        const emailError = new Error('A user with this email address already exists');
        (emailError as any).code = 'EMAIL_ALREADY_EXISTS';
        (emailError as any).status = 409;
        (emailError as any).field = 'email';
        throw emailError;
      }
      
      throw error;
    }
  }

  /**
   * Update an existing service provider user
   */
  async updateUser(userId: string, userData: UpdateServiceProviderUserRequest): Promise<ServiceProviderUser> {
    // Build the PUT request payload matching the API schema exactly
    const dateOfBirth = this.normalizeDateOfBirth(userData.dateOfBirth);

    const updatePayload: any = {
      id: userId, // Include the ID in the request body
      ...(userData.firstName !== undefined && { firstName: userData.firstName }),
      ...(userData.lastName !== undefined && { lastName: userData.lastName }),
      ...(userData.name !== undefined && { name: userData.name }),
      ...(userData.email !== undefined && { email: userData.email }),
      ...(userData.mobileNumber !== undefined && { mobileNumber: userData.mobileNumber }),
      userType: 'External', // Always External for service provider users
      userRoles: userData.userRoles || [],
      dateOfBirth,
      hireDate: userData.hireDate ? new Date(userData.hireDate).toISOString() : null,
      ...(userData.serviceProviderId ? { serviceProviderId: userData.serviceProviderId } : {}),
      authorizedDistricts: userData.authorizedDistricts || [],
      authorizedStates: userData.authorizedStates || [],
      authorizedDispatchAreas: userData.authorizedDispatchAreas || []
    };

    const response = await this.fetchWithAuth<ServiceProviderUser>(
      `/Users/${userId}`,
      {
        method: 'PUT',
        body: JSON.stringify(updatePayload),
      }
    );

    // Handle both response formats: direct user object or wrapped in data property
    const updatedUser = (response as any).data || response;

    if (!updatedUser || !updatedUser.id) {
      throw new Error('API response missing user data');
    }
    return updatedUser as ServiceProviderUser;
  }

  /**
   * Delete a service provider user
   */
  async deleteUser(userId: string): Promise<void> {
    await this.fetchWithAuth<void>(`/Users/${userId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Map API user data to ServiceProviderUser format
   */
  private mapApiUserToServiceProviderUser(apiUser: any): ServiceProviderUser {
    // No static fallback maps - return raw IDs and let UI layer handle mapping
    return {
      id: apiUser.id,
      name: apiUser.name || '',
      ...(typeof apiUser.firstName === 'string' && apiUser.firstName.trim() !== ''
        ? { firstName: apiUser.firstName }
        : {}),
      ...(typeof apiUser.lastName === 'string' && apiUser.lastName.trim() !== ''
        ? { lastName: apiUser.lastName }
        : {}),
      email: apiUser.email || '',
      mobileNumber: apiUser.mobileNumber || '',
      dateOfBirth: apiUser.dateOfBirth || '',
      hireDate: apiUser.hireDate || '',
      role: apiUser.userRole || '',
      roleId: apiUser.userRole || '',
      userRoles: apiUser.userRoles || [],
      status: apiUser.invitationStatus || 'NotStarted',
      isActive: apiUser.isActive !== undefined ? apiUser.isActive : true,
      isActivated: apiUser.isActivated,
      assignedAreas: {
        states: (apiUser.authorizedStates || []).map((item: any) => HttpClient.mapAreaItem(item)),
        dispatchAreas: (apiUser.authorizedDispatchAreas || []).map((item: any) => HttpClient.mapAreaItem(item)),
        districts: (apiUser.authorizedDistricts || []).map((item: any) => HttpClient.mapAreaItem(item))
      },
      createdAt: apiUser.createdAt || new Date().toISOString(),
      updatedAt: apiUser.updatedAt || new Date().toISOString(),
      lastLogin: apiUser.lastLoginAt,
      invitedAt: apiUser.createdAt,
      ...(apiUser.serviceProviderId !== undefined && apiUser.serviceProviderId !== null
        ? { serviceProviderId: apiUser.serviceProviderId }
        : {}),
      authorizedDistricts: apiUser.authorizedDistricts || [],
      authorizedStates: apiUser.authorizedStates || [],
      authorizedDispatchAreas: apiUser.authorizedDispatchAreas || [],
    };
  }

  /**
   * Get user by ID
   */
  async getUserById(userId: string): Promise<ServiceProviderUser> {
    const response = await this.fetchWithAuth<ServiceProviderUser>(
      `/Users/${userId}`
    );

    // Get the API user data (handle both response formats)
    const apiUser = (response as any).data || (response as any);

    if (!apiUser || !apiUser.id) {
      throw new Error('API response missing user data');
    }

    // Map API response to ServiceProviderUser format
    return this.mapApiUserToServiceProviderUser(apiUser);
  }

  /**
   * Get available roles for user assignment
   */
  async getAvailableRoles(): Promise<UserRole[]> {
    try {
      const response = await this.fetchWithAuth<UserRole[]>(
        '/Roles',
        { method: 'GET' }
      );

      // Handle both response formats
      const data = response.data || response;
      // 🎭 Roles API response: data
      return Array.isArray(data) ? data : [];
    } catch (error) {
      return [];
    }
  }



  /**
   * Update user roles
   */
  async updateUserRoles(userId: string, roleIds: string[]): Promise<ServiceProviderUser> {
    const response = await this.fetchWithAuth<ServiceProviderUser>(
      `/Users/${userId}/roles`,
      {
        method: 'PUT',
        body: JSON.stringify({ roleIds }),
      }
    );

    if (!response || !response.data) {
      throw new Error('API response missing user data');
    }
    return response.data;
  }

  /**
   * Bulk delete users
   */
  async bulkDeleteUsers(userIds: string[]): Promise<void> {
    await this.fetchWithAuth<void>(
      '/Users/bulk-delete',
      {
        method: 'DELETE',
        body: JSON.stringify({ userIds }),
      }
    );
  }

  /**
   * Deactivate users (single or multiple)
   */
  async deactivateUsers(userIds: string[]): Promise<{
    deactivatedUserIds: string[];
    failedUserIds: { userId: string; reason: string }[];
    totalRequested: number;
    totalDeactivated: number;
    totalFailed: number;
  }> {
    const response = await this.fetchWithAuth<{
      deactivatedUserIds: string[];
      failedUserIds: { userId: string; reason: string }[];
      totalRequested: number;
      totalDeactivated: number;
      totalFailed: number;
    }>(
      '/Users/deactivate',
      {
        method: 'POST',
        body: JSON.stringify({ userIds }),
      }
    );
    // API returns data directly, not wrapped in ApiResponse
    return (response.data || response) as {
      deactivatedUserIds: string[];
      failedUserIds: { userId: string; reason: string }[];
      totalRequested: number;
      totalDeactivated: number;
      totalFailed: number;
    };
  }

  /**
   * Deactivate single user
   */
  async deactivateUser(userId: string): Promise<void> {
    await this.deactivateUsers([userId]);
  }

  /**
   * Resend invitation to user
   */
  async resendInvitation(userId: string): Promise<void> {
    await this.fetchWithAuth<void>(
      `/Users/${userId}/invite/resend`,
      { method: 'POST' }
    );
  }

  /**
   * Revoke user invitation
   */
  async revokeInvitation(userId: string): Promise<void> {
    await this.fetchWithAuth<void>(
      `/Users/${userId}/invite/revoke`,
      { method: 'POST' }
    );
  }

  /**
   * Get invitation status for user
   */
  async getInvitationStatus(userId: string): Promise<InvitationStatus> {
    const response = await this.fetchWithAuth<InvitationStatus>(
      `/Users/${userId}/invitation`,
      { method: 'GET' }
    );

    // Handle both response formats - check if it's wrapped in ApiResponse
    if ('data' in response && response.data) {
      return response.data;
    }
    // If not wrapped, the response itself is the InvitationStatus
    return response as unknown as InvitationStatus;
  }

  /**
   * Fetch ALL users belonging to a specific service provider (no server-side pagination).
   * Uses GET /Users/service-provider/{serviceProviderId} which returns a flat List<UserDto>.
   * Client-side pagination, search and active/inactive filtering are applied by the caller.
   */
  async fetchAllUsersByServiceProvider(serviceProviderId: string): Promise<ServiceProviderUser[]> {
    const response = await this.fetchWithAuth<any>(
      `/Users/service-provider/${serviceProviderId}`,
      { method: 'GET' }
    );
    // The endpoint returns List<UserDto> directly (not wrapped in PaginatedResult)
    let items: any[] = [];
    if (Array.isArray(response)) {
      items = response;
    } else if (Array.isArray((response as any)?.data)) {
      items = (response as any).data;
    }
    return items.map((apiUser) => this.mapApiUserToServiceProviderUser(apiUser));
  }
}

// Export singleton instance
export const serviceProviderUserService = new ServiceProviderUserService();
export default serviceProviderUserService;